export const STAT_KEYS = ["str", "atk", "def", "agi", "vit", "sta", "cha"] as const;
export type StatKey = (typeof STAT_KEYS)[number];
export type Stats = Record<StatKey, number>;

export type CombStyle = "single" | "rose" | "crest" | "mohawk" | "snood" | "none";
export type TailStyle = "fluffy" | "sickle" | "fan" | "stub";
export type Pattern = "solid" | "speckled" | "barred" | "laced";
export type EyeStyle = "normal" | "fierce" | "sleepy" | "shades";
export type Extra = "none" | "eyepatch" | "scar" | "bandana" | "monocle" | "mustache" | "bowtie";
export type BeakStyle = "pointy" | "hooked" | "duck" | "short";
export type BodyShape = "round" | "penguin";

export interface BirdLook {
  kind: "bird";
  body: string;
  wing: string;
  tail: string;
  head?: string;
  belly?: string;
  comb: string;
  beak: string;
  legs: string;
  combStyle: CombStyle;
  tailStyle: TailStyle;
  pattern: Pattern;
  patternColor?: string;
  eyes: EyeStyle;
  extra: Extra;
  extraColor?: string;
  beakStyle: BeakStyle;
  wattle: boolean;
  size: number;
  neck: number;
  legLen: number;
  shape: BodyShape;
}

export type HatStyle = "straw" | "cowboy" | "top" | "none" | "chef";
export interface HumanLook {
  kind: "human";
  skin: string;
  hair: string;
  shirt: string;
  pants: string;
  jacket?: string;
  hat: HatStyle;
  beard: "none" | "goatee" | "stubble" | "mustache";
  glasses: boolean;
  tie?: string;
  overalls?: boolean;
  badge?: boolean;
  sash?: string;
  size: number;
}

export type Look = BirdLook | HumanLook;

export type Slot = "weapon" | "shield" | "helmet" | "armor" | "boots";
export const SLOTS: Slot[] = ["weapon", "shield", "helmet", "armor", "boots"];

export type Material = "wood" | "corn" | "rusty" | "bronze" | "iron" | "steel" | "gold" | "egg" | "cosmic" | "leather" | "cloth";
export type WeaponShape = "stick" | "club" | "knife" | "spork" | "sword" | "axe" | "mace" | "spear" | "fork" | "cleaver" | "hammer" | "scythe" | "drumstick";
export type ShieldShape = "lid" | "round" | "kite" | "tower" | "egg";
export type HelmetShape = "eggshell" | "pot" | "viking" | "gladiator" | "knight" | "crown";
export type ArmorShape = "sack" | "leather" | "plate" | "chain" | "royal";
export type BootShape = "sandals" | "greaves" | "winged";

interface ItemBase {
  id: string;
  name: string;
  price: number;
  tier: number;
  material: Material;
  desc: string;
  bonus?: Partial<Stats>;
  req?: Partial<Stats>;
}
export interface WeaponItem extends ItemBase {
  slot: "weapon";
  shape: WeaponShape;
  min: number;
  max: number;
  acc: number;
  reach: 1 | 2;
  twoHanded: boolean;
}
export interface ShieldItem extends ItemBase {
  slot: "shield";
  shape: ShieldShape;
  block: number;
  armor: number;
}
export interface HelmetItem extends ItemBase {
  slot: "helmet";
  shape: HelmetShape;
  armor: number;
}
export interface ArmorItem extends ItemBase {
  slot: "armor";
  shape: ArmorShape;
  armor: number;
}
export interface BootItem extends ItemBase {
  slot: "boots";
  shape: BootShape;
  armor: number;
}
export type Item = WeaponItem | ShieldItem | HelmetItem | ArmorItem | BootItem;

export type Gear = Partial<Record<Slot, string>>;

export type SpecialId = "egg" | "roar" | "frenzy" | "wind";

export interface Hero {
  name: string;
  look: BirdLook;
  base: Stats;
  level: number;
  xp: number;
  points: number;
  gold: number;
  inventory: string[];
  gear: Gear;
  specials: SpecialId[];
  /** Index of the next story fight (0-based across all chapters). */
  progress: number;
  wins: number;
  losses: number;
  seenScenes: string[];
  finished: boolean;
}

export type Archetype = "brute" | "duelist" | "tank" | "showman" | "balanced";

export interface Opponent {
  id: string;
  name: string;
  title: string;
  look: Look;
  level: number;
  archetype: Archetype;
  gear: Gear;
  taunt: string;
  boss?: boolean;
  specials?: SpecialId[];
  power?: number;
}

export type Backdrop = "barn" | "fair" | "city" | "capitol" | "summit" | "colosseum";
